import { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../services/apiService";
import { OnboardingData } from "../types/onboarding";
import { saveOnboardingData } from "../utils/storage";

const DEFAULT_DATA: OnboardingData = {
  firstName: "",
  lastName: "",
  companyName: "",
  companyWebsite: "",
  role: "",
  selectedContacts: [],
  metrics: [],
  engineeringMetrics: [],
  integrations: [],
  step: 1,
  completed_steps: [],
  onboarding_complete: false,
};

export function useOnboardingFlow() {
  const [data, setData] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiService.get("/user/onboarding");
        const server: OnboardingData | null = res?.data?.onboarding || null;
        if (!mounted) return;
        setData(server || DEFAULT_DATA);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || "Failed to load onboarding");
        setData(DEFAULT_DATA);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const save = useCallback(
    async (partial: Partial<OnboardingData>) => {
      const next: OnboardingData = {
        ...(data || DEFAULT_DATA),
        ...partial,
      } as OnboardingData;
      setData(next);
      await apiService.post("/user/onboarding/update", { onboarding: next });
      
      // Sync to localStorage after successful save to keep cache up-to-date
      saveOnboardingData(next);
      
      return next;
    },
    [data]
  );

  const complete = useCallback(async () => {
    const current = data || DEFAULT_DATA;
    const next: OnboardingData = {
      ...current,
      onboarding_complete: true,
      step: 8,
      completed_steps: Array.from(
        new Set([...(current.completed_steps || []), "payment_complete"]) as any
      ),
    } as OnboardingData;
    setData(next);
    await apiService.post("/user/onboarding-complete", { onboarding: next });

    // Sync to localStorage after successful completion
    saveOnboardingData(next);
  }, [data]);

  /**
   * Complete portfolio company onboarding (for VC-invited users).
   * This creates the VC-managed workflow and marks onboarding complete.
   */
  const completePortfolio = useCallback(async (): Promise<{ success: boolean; workflow?: any; error?: string }> => {
    const current = data || DEFAULT_DATA;
    const next: OnboardingData = {
      ...current,
      onboarding_complete: true,
      step: 8,
      completed_steps: Array.from(
        new Set([...(current.completed_steps || []), "payment_complete"]) as any
      ),
    } as OnboardingData;

    try {
      // Call the portfolio-specific completion endpoint
      const response = await apiService.post("/user/onboarding/complete-portfolio", {
        onboarding: next,
      });

      setData(next);
      saveOnboardingData(next);

      return {
        success: true,
        workflow: response?.data?.workflow,
      };
    } catch (e: any) {
      console.error("[useOnboardingFlow] completePortfolio error:", e);
      return {
        success: false,
        error: e?.response?.data?.error || e?.message || "Failed to complete portfolio onboarding",
      };
    }
  }, [data]);

  const currentStep = useMemo(
    () => (data?.step ? Math.min(Math.max(data.step, 1), 8) : 1),
    [data?.step]
  );
  const isComplete = data?.onboarding_complete === true;

  return {
    data: data || DEFAULT_DATA,
    loading,
    error,
    save,
    complete,
    completePortfolio,
    currentStep,
    isComplete,
  };
}
